<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        CHANNELS,
        currency,
        Heading,
        Icon,
        MailToButton,
        number,
        OutputAuthenticityCode,
        OutputBrands,
        OutputCategories,
        OutputChannels,
        OutputLocations,
        OutputMarkets,
        OutputPayment,
        OutputShippingOptions,
        OutputVertical,
        Paragraph,
        Span,
    } from '@smatch/cassiopeia-web-components';

    // COMPONENTS
    import { BadgePartialBid } from 'src/app/routes/auctions/components/index.js';

    // UTILS
    import { REPORT_LEGAL_MAIL_BODY } from 'src/app/routes/auctions/utils/mail-body';

    /* TYPES ----------------------------------------------------------------------------------- */

    import type { Auction } from 'src/app/types';
    type Channels = NonNullable<Auction['blacklist']>['channelCodes'];

    /* EXPORTS --------------------------------------------------------------------------------- */

    export let auction: Auction;

    /* INTERNALS -------------------------------------------------------------------------------- */

    let tab = 'summary';

    $: ({ payments, shipping, addressKeys } = auction);

    const getChannels = (channelCodes: Channels = []): Channels => CHANNELS
        .map(({ key }) => key)
        .filter(code => !channelCodes.includes(code));

    const switchTab = (newTab: string): void => {
        tab = newTab;
    };

    $: channels = getChannels(auction.blacklist?.channelCodes);
    $: markets = auction.blacklist?.marketCodes;

</script>

<div id = 's8-auction-item-terms'>

    <div class = 's8-wrap'>

        <aside>
            <ul class = 'mb-6'>
                <li on:click = { switchTab.bind(null, 'summary') } on:keypress = { switchTab.bind(null, 'summary') }>
                    <Icon key = 'arrowRight' css = 'h-3 -top-px relative !inline-block'/> Summary
                </li>
                <li on:click = { switchTab.bind(null, 'terms') } on:keypress = { switchTab.bind(null, 'terms') }>
                    <Icon key = 'arrowRight' css = 'h-3 -top-px relative !inline-block'/> Terms
                </li>
                <li on:click = { switchTab.bind(null, 'payment') } on:keypress = { switchTab.bind(null, 'payment') }>
                    <Icon key = 'arrowRight' css = 'h-3 -top-px relative !inline-block'/> Payment & Shipping
                </li>
            </ul>
        </aside>

        {#if tab === 'summary'}

            <article>

                <Heading size = 'sm'>{ auction.name }</Heading>

                <Heading size = 'xs'>Industry</Heading>
                <Paragraph>
                    <OutputVertical code = { auction.verticalCode } />
                </Paragraph>

                {#if Array.isArray(auction.brands) && auction.brands.length > 0}
                    <Heading size = 'xs'>Brands</Heading>
                    <OutputBrands
                        keys = { auction.brands.map(({ key }) => key) }
                        verticalCodes = { [ auction.verticalCode ] }
                        auctionKey = { auction._.key }
                    />
                {/if}

                {#if Array.isArray(auction.categories) && auction.categories.length > 0}
                    <Heading size = 'xs'>Categories</Heading>
                    <OutputCategories
                        keys = { auction.categories.map(({ key }) => key) }
                        verticalCode = { auction.verticalCode }
                        auctionKey = { auction._.key }
                    />
                {/if}

                {#if addressKeys !== undefined && addressKeys.length > 0 }
                    <Heading size = 'xs'>Points of Origin</Heading>
                    <Paragraph>
                        <OutputLocations { addressKeys } />
                    </Paragraph>
                {/if}

                <MailToButton
                    label = 'Report Legal Issue'
                    to = 'fraud@smatch.com'
                    subject = { `Reporting a Legal Issue regarding: ${ auction.name }` }
                    body = { `${ REPORT_LEGAL_MAIL_BODY } ${ auction._.key }` }
                    color = 'dark'
                    css = 'mt-2'
                />
            </article>

        {/if}

        {#if tab === 'terms'}

            <article>

                <Heading size = 'sm'>Terms</Heading>

                {#if auction.allowsPartialBids}
                    <Heading size = 'xs'>Partial Bids</Heading>
                    <Paragraph>
                        This is a
                        <BadgePartialBid allowsPartialBids = { auction.allowsPartialBids } />
                        auction. <br />
                        <Span css = 'text-grey-600'>Bids can be placed for a selection of items in the auction
                        instead of the full set. Our account manager will contact you once the bid is
                        submitted to confirm your selection of items.</Span>
                    </Paragraph>
                {:else}
                    <Heading size = 'xs'>Take-All Bids</Heading>
                    <Paragraph>
                        This is a
                        <BadgePartialBid allowsPartialBids = { auction.allowsPartialBids } />
                        auction.
                        <Span css = 'text-grey-600'>
                            Bids must be placed on all items in the auction.
                        </Span>
                    </Paragraph>
                {/if}

                {#if auction.bid?.minimum?.value}
                    <Heading size = 'xs'>Minimum Order Value</Heading>
                    <Paragraph>
                        { currency(auction.bid.minimum.value, auction.locale.currencyCode, false) }
                    </Paragraph>
                {/if}

                {#if auction.bid?.minimum?.items}
                    <Heading size = 'xs'>Minimum Order Quantity</Heading>
                    <Paragraph>
                        { number(auction.bid.minimum.items) } Items
                    </Paragraph>
                {/if}
                {#if auction.enableFulfilmentViaSmatch}
                    <Heading size = 'xs'>Fullfilment via Smatch</Heading>
                    <Paragraph>
                        Upon request, smatch handles all logistics for your order,
                        including packaging, storage, and transportation to your warehouse.
                    </Paragraph>
                {/if}
                <Heading size = 'xs'>Proof of Authenticity</Heading>
                <Paragraph>
                    <OutputAuthenticityCode code = { auction.authenticityCode} />
                </Paragraph>

                <Heading size = 'xs'>Preferred Channels</Heading>
                <Paragraph>
                    {#if Array.isArray(channels) && channels.length > 0}
                        <OutputChannels codes = { channels } />
                    {/if}
                </Paragraph>

                <Heading size = 'xs'>Excluded Markets</Heading>
                <Paragraph>
                    {#if Array.isArray(markets) && markets.length > 0}
                        <OutputMarkets keys = { markets } />
                    {:else}
                        No excluded markets.
                    {/if}
                </Paragraph>

            </article>

        {/if}

        {#if tab === 'payment'}

            <article>

                <Heading size = 'sm'>Payment & Shipping</Heading>

                {#if Array.isArray(payments) && payments.length > 0}
                    <Heading size = 'xs'>Payment</Heading>
                    <OutputPayment { payments } />
                {/if}

                <Heading size = 'xs'>Currency</Heading>
                <Paragraph>
                    Payment is in { auction.locale.currencyCode }.
                </Paragraph>

                {#if addressKeys !== undefined && addressKeys.length > 0 }
                    <Heading size = 'xs'>Points of Origin</Heading>
                    <Paragraph>
                        <OutputLocations { addressKeys } />
                    </Paragraph>
                {/if}

                {#if shipping }

                    {#if Array.isArray(shipping.options) && shipping.options.length > 0}
                        <Heading size = 'xs'>Shipment Terms</Heading>
                        <Paragraph>
                            The seller offers following shipment options:
                        </Paragraph>
                        <OutputShippingOptions options = { shipping.options } />
                    {/if}

                {/if}

            </article>

        {/if}

    </div>

</div>

<style lang='postcss'>

    div#s8-auction-item-terms {
        container-name: terms;
        container-type: inline-size;
        width: 600px;
        max-width: 70vw;

        div.s8-wrap {
            display: flex;
            min-height: 50vh;
            column-gap: 20px;
            row-gap: 20px;

            aside {
                background-color: rgba(39,39,17,0.04);
                border-radius: 10px;
                padding: 20px;
                min-width: fit-content;

                ul {
                    margin: 0px;

                    li {
                        cursor: pointer;
                    }
                }
            }

            article {
                :global(ul), :global(p) {
                    padding-left: 20px;
                    margin-bottom: 10px;
                }
            }
        }
    }

    @container terms (max-width: 450px) {
        div#s8-auction-item-terms {
           div.s8-wrap {
                flex-direction: column;
           }
        }
    }

</style>